import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-latest-qr-2026': 'https://readdy.ai/api/search-image?query=Modern%20clean%20technology%20concept%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20QR%20code%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-latest-qr-2026&orientation=landscape',
  'article-scan-tutorial': 'https://readdy.ai/api/search-image?query=Modern%20technology%20education%20illustration%20with%20warm%20amber%20tones%2C%20smartphone%20scanning%20concept%2C%20minimalist%20design%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-scan-tutorial&orientation=landscape',
  'article-safety-guide': 'https://readdy.ai/api/search-image?query=Modern%20security%20shield%20concept%20illustration%20with%20warm%20coral%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20protection%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-safety-guide&orientation=landscape',
  'article-invalid-solution': 'https://readdy.ai/api/search-image?query=Modern%20problem%20solving%20illustration%20with%20warm%20amber%20and%20gray%20tones%2C%20minimalist%20design%2C%20abstract%20connection%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-invalid-solution&orientation=landscape',
  'article-mobile-guide': 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20mobile%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-mobile-guide&orientation=landscape',
  'article-registration': 'https://readdy.ai/api/search-image?query=Modern%20signup%20registration%20illustration%20with%20warm%20amber%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20user%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-registration&orientation=landscape',
  'article-faq-summary': 'https://readdy.ai/api/search-image?query=Modern%20FAQ%20help%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20question%20mark%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-faq-summary&orientation=landscape',
  'article-vpn-guide': 'https://readdy.ai/api/search-image?query=Modern%20network%20security%20illustration%20with%20warm%20amber%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20connection%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-vpn-guide&orientation=landscape',
  'article-safety-tips': 'https://readdy.ai/api/search-image?query=Modern%20safety%20warning%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20alert%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-safety-tips&orientation=landscape',
  'article-features': 'https://readdy.ai/api/search-image?query=Modern%20community%20platform%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20features%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=1200&height=600&seq=article-features&orientation=landscape',
};

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
});

function getArticleSchema(article: typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishedAt,
        dateModified: article.updatedAt,
        url: `https://caoliu-qrcode.example.com/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: '草榴二维码指南',
        },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://caoliu-qrcode.example.com',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: categoryNameMap[article.category] || article.category,
            item: `https://caoliu-qrcode.example.com/category/${article.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-file-unknow-line text-5xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryName = categoryNameMap[article.category] || article.category;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 草榴二维码指南`}
        description={article.excerpt}
        keywords={`草榴二维码,${categoryName},${article.title},草榴社区二维码`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        schemaData={getArticleSchema(article)}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: t('common.breadcrumb.home'), to: '/' },
              { label: categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <img
              src={articleImages[article.imageSeq]}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover object-top rounded-lg"
              title={article.title}
            />
          </div>

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {categoryName}
              </Link>
              <span className="text-xs text-foreground-500">
                {t('article.publishedAt')} {article.publishedAt}
              </span>
              {article.updatedAt !== article.publishedAt && (
                <span className="text-xs text-foreground-500">
                  {t('article.updatedAt')} {article.updatedAt}
                </span>
              )}
              <span className="text-xs text-foreground-500">
                {article.readTime} {t('article.minRead')}{t('article.readTime')}
              </span>
              <span className="text-xs text-foreground-500">
                {t('article.author')}: {article.author}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
          </header>

          <div
            className="prose prose-lg max-w-none mb-12 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 pt-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mb-8 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              {t('article.backToList')}
            </Link>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-8 pt-8 border-t border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                {t('article.relatedTitle')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group p-4 rounded-lg border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 cursor-pointer bg-background-50"
                  >
                    <h3 className="text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {related.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}